import React, { memo, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ChatContext } from 'context/Context';
import { FormControl, Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { searchChats, getChatById } from 'services/elvis';
import dayjs from 'dayjs';

const Highlight = ({ question, query }) => {
    const keyword = query?.trim();

    if (!question) return null;
    if (!keyword) return <p>{question}</p>;

    // Escape the query so one containing regex syntax - "c++", "(", "?" - is
    // matched literally instead of blowing up new RegExp
    const pattern = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Splitting on a capturing group keeps the matches in the output, so every
    // odd index is an occurrence of the query and every even index is the text
    // around it. Case-insensitive, so the question keeps its own casing.
    const parts = question.split(new RegExp(`(${pattern})`, 'gi'));

    return (
        <p className="text-truncate mb-0" title={question}>
            {parts.map((part, index) =>
                index % 2 === 1 ? <mark key={index}>{part}</mark> : part
            )}
        </p>
    );
}

Highlight.propTypes = {
    question: PropTypes.string,
    query: PropTypes.string
};

const SearchModal = ({ selectThread }) => {
    const { threads, threadsDispatch, showSearchModal, setShowSearchModal } = useContext(ChatContext);
    const [query, setQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);

    const handleSearch = async keyword => {
        try {
            setLoading(true);
            const res = await searchChats(keyword);
            setSearchResults(res.data || []);
        } catch (err) {
            console.log(err);
            toast.error('Error searching chats! Please try again later.');
        } finally {
            setLoading(false);
        }
    }

    const clearData = () => {
        setSearchResults([]);
        setQuery('');
        setLoading(false);
        setShowSearchModal(false);
    }

    const selectChat = async id => {
        const loaded = threads.find(t => t.id === id);
        if (loaded) {
            selectThread(loaded);
            clearData();
            return;
        }

        try {
            const res = await getChatById(id);
            if (!res?.length) {
                toast.error('That chat could not be loaded!');
                return;
            }
            const copy = [...threads];
            // Keep the new-chat placeholder at index 0, drop the fetched chat in after it
            copy.splice(1, 0, ...res);
            // Dispatch before selecting so the thread is in the list by the time
            // activeKey is recalculated - the other order renders one frame with no
            // active pane. Select the object rather than an index: an index would be
            // resolved against whichever threads array the caller last closed over.
            threadsDispatch({ type: 'SET', payload: copy });
            selectThread(res[0]);
        } catch (err) {
            console.error(err);
            toast.error('Error fetching chat! Please try again later.');
            return;
        }
        clearData();
    }

    useEffect(() => {
        const keyword = query.trim();
        if (!keyword) {
            setSearchResults([]);
            return;
        }
        const delayDebounce = setTimeout(() => handleSearch(keyword), 1500);
        return () => clearTimeout(delayDebounce);
    }, [query]);

    return (
        <Modal
            show={showSearchModal}
            onHide={() => clearData()}
            onEscapeKeyDown={() => clearData()}>
            <Modal.Header>
                <FormControl
                    className="border-0 shadow-none mx-2 p-2"
                    placeholder="Search chats..."
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                />
                <kbd
                    className="mx-2 p-2 fs--2 bg-100 text-700 border flex-shrink-0 shadow-sm cursor-pointer"
                    onClick={() => clearData()}
                >ESC</kbd>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {loading && <Spinner variant="primary" animation="border" className="d-block mx-auto" />}
                {searchResults.length > 0 ? searchResults.map((result, index) => (
                    <div
                        key={index}
                        className="p-2 mx-4 my-2 bg-100 rounded-3 shadow-sm cursor-pointer"
                        onClick={async () => await selectChat(result.conversationId)}
                    >
                        <Highlight question={result.question} query={query} />
                        <p className="text-muted small mt-0">
                            <span>Asked: </span>{dayjs(result.createdDate).format('DD/MM/YYYY HH:mm')}
                        </p>
                    </div>
                )) : (
                    <p className="text-center text-muted">{loading ? "Searching chats..." : "No chats found."}</p>
                )}
            </Modal.Body>
        </Modal>
    )
}

SearchModal.propTypes = {
    selectThread: PropTypes.func
};

export default memo(SearchModal);
